<div class="flex min-h-screen flex-col bg-slate-100">
  <!-- Exam header: title -->
  <header class="flex shrink-0 items-center justify-center border-b border-[#2d5aa8] bg-[#3F71C0] px-4 py-3 shadow-sm">
    <span class="text-sm font-medium text-white">TCF Canada · Expression écrite</span>
  </header>

  @if (combination(); as c) {
    <div class="flex min-h-0 flex-1 overflow-hidden">
      <!-- Left: Tâche 1, 2, 3 vertically -->
      <aside class="flex w-48 shrink-0 flex-col border-r border-[#2d5aa8] bg-[#3F71C0]">
        @for (task of [1, 2, 3]; track task; let i = $index) {
          <button
            type="button"
            class="flex items-center gap-3 border-b border-[#2d5aa8]/50 px-4 py-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-inset"
            [class.bg-[#5F93D0]]="activeTaskIndex() === i"
            [class.font-semibold]="activeTaskIndex() === i"
            [class.text-white]="activeTaskIndex() === i"
            [class.text-white/90]="activeTaskIndex() !== i"
            [class.hover:bg-[#4a7fc9]]="activeTaskIndex() !== i"
            (click)="setActiveTask(i)"
          >
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-medium"
              [class.bg-white]="activeTaskIndex() === i"
              [class.text-[#3F71C0]]="activeTaskIndex() === i"
              [class.bg-white/20]="activeTaskIndex() !== i"
              [class.text-white]="activeTaskIndex() !== i"
            >
              {{ task }}
            </span>
            <span>Tâche {{ task }}</span>
          </button>
        }
      </aside>

      <!-- Center: topic (top) + input area (rest) -->
      <section class="flex min-w-0 flex-1 flex-col overflow-hidden bg-white">
        <!-- Top: topic/prompt -->
        @if (activeTask(); as task) {
          <div class="shrink-0 overflow-y-auto border-b border-slate-200 bg-slate-50 p-4">
            @switch (activeTaskIndex()) {
              @case (0) {
                <h2 class="mb-2 text-sm font-semibold text-slate-700">Tâche 1</h2>
                <p class="mb-3 text-xs text-slate-500">60 mots minimum, 120 mots maximum</p>
              }
              @case (1) {
                <h2 class="mb-2 text-sm font-semibold text-slate-700">Tâche 2</h2>
                <p class="mb-3 text-xs text-slate-500">120 mots minimum, 150 mots maximum</p>
              }
              @case (2) {
                <h2 class="mb-2 text-sm font-semibold text-slate-700">Tâche 3</h2>
                <p class="mb-2 text-xs text-slate-500">Vous écrivez un message (120 mots minimum / 180 mots maximum) que vous voulez publier sur ce site. Votre message comprend deux parties :</p>
                <ul class="mb-3 list-inside list-disc space-y-1 text-xs text-slate-600">
                  <li>dans la première partie, vous présentez les deux opinions avec vos propres mots (entre 40 et 60 mots);</li>
                  <li>dans la deuxième partie, vous donnez votre position sur le thème général, commun à ces deux opinions (entre 80 et 120 mots).</li>
                </ul>
              }
            }
            @if (activeTaskIndex() === 2 && task.documents.length) {
              <div class="mt-3 space-y-3">
                <!-- Task 3 main theme / title above documents -->
                <p class="text-sm font-medium leading-relaxed text-slate-800">
                  {{ task.prompt }}
                </p>

                <!-- Documents 1 & 2, full width -->
                @for (doc of task.documents; track $index) {
                  <div class="rounded-xl border border-blue-100 bg-blue-50/70 p-3.5">
                    <div class="mb-1 flex items-center gap-2">
                      <span
                        class="inline-flex items-center justify-center rounded-full bg-blue-600 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white"
                      >
                        Document {{ $index + 1 }}
                      </span>
                    </div>
                    <p class="text-sm leading-relaxed text-slate-800">
                      {{ doc }}
                    </p>
                  </div>
                }
              </div>
            } @else {
              <p class="leading-relaxed text-slate-800">
                {{ task.prompt }}
              </p>
            }
          </div>
        }

        <!-- Rest: input area -->
        <div class="flex min-h-0 flex-1 flex-col p-4">
          <label for="exam-answer" class="mb-2 block text-sm font-medium text-slate-700">Votre réponse</label>
          <textarea
            id="exam-answer"
            spellcheck="false"
            [ngModel]="currentAnswer()"
            (ngModelChange)="updateAnswer($event)"
            (paste)="config.allowCopyPaste() || $event.preventDefault()"
            (cut)="config.allowCopyPaste() || $event.preventDefault()"
            (copy)="config.allowCopyPaste() || $event.preventDefault()"
            class="min-h-[200px] w-full flex-1 resize-none rounded-lg border border-slate-300 bg-white p-4 font-sans text-base text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            placeholder="Écrivez votre réponse ici..."
            rows="8"
          ></textarea>
        </div>
      </section>

      <!-- Right: countdown + word count + virtual French keyboard + buttons -->
      <aside class="flex w-64 shrink-0 flex-col border-l border-[#2d5aa8] bg-[#3F71C0] p-4">
        <!-- Countdown -->
        <div class="mb-4 animate-timer-pulse">
          <h3 class="mb-2 text-xs font-semibold uppercase tracking-wider text-white/80">Temps restant</h3>
          <span class="font-mono text-2xl font-bold tabular-nums text-white" aria-live="polite">
            {{ timerDisplay() }}
          </span>
        </div>

        <!-- Word count -->
        <div class="mb-4">
          <h3 class="mb-2 text-xs font-semibold uppercase tracking-wider text-white/80">Mots</h3>
          <p
            class="text-2xl font-bold tabular-nums"
            [class.text-amber-300]="wordCount() > 0 && !isWithinLimit()"
            [class.text-white]="wordCount() === 0 || isWithinLimit()"
          >
            {{ wordCount() }}
          </p>
          <p class="mt-0.5 text-xs text-white/80">
            {{ wordLimit().min }}–{{ wordLimit().max }} requis
          </p>
        </div>

        <!-- Virtual French keyboard -->
        <div class="mb-4 rounded-lg border border-slate-200 bg-white p-3">
          <div class="mb-2 flex items-center justify-between gap-2">
            <h3 class="text-xs font-semibold uppercase tracking-wider text-slate-500">Caractères spéciaux</h3>
            <button
              type="button"
              class="flex h-8 min-w-[2rem] items-center justify-center rounded-md border border-slate-200 bg-slate-100 px-2 text-xs font-semibold text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              (click)="toggleSpecialCharsCase()"
              [attr.aria-pressed]="specialCharsUpperCase()"
              title="切换大小写"
            >
              A ↔ a
            </button>
          </div>
          <div class="flex flex-wrap gap-1.5">
            @for (row of frenchChars(); track $index) {
              @for (char of row; track char) {
                <button
                  type="button"
                  class="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-base font-medium text-slate-700 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  (click)="insertChar(char)"
                >
                  {{ char }}
                </button>
              }
            }
          </div>
        </div>

        <!-- Buttons at bottom -->
        <div class="mt-auto flex flex-col gap-2 pt-4">
          @if (submitError()) {
            <p class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {{ submitError() }}
            </p>
          }
          <button
            type="button"
            class="w-full rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-[#3F71C0] transition-colors hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#3F71C0] disabled:opacity-50 disabled:cursor-not-allowed"
            (click)="submitExam(true)"
            [disabled]="submitting()"
          >
            {{ submitting() ? '提交中...' : '交卷' }}
          </button>
          <button
            type="button"
            class="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/40 bg-white/10 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#3F71C0]"
            (click)="exitExam()"
          >
            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            退出
          </button>
        </div>
      </aside>
    </div>
  } @else if (loading()) {
    <div class="flex flex-1 items-center justify-center">
      <p class="text-slate-500">加载中...</p>
    </div>
  } @else if (outOfRange()) {
    <div class="flex flex-1 items-center justify-center">
      <p class="rounded-lg border border-amber-200 bg-amber-50 px-6 py-4 text-amber-700 shadow-sm">
        该月份的题目不可用，将在 2 秒后返回题库...
      </p>
    </div>
  } @else {
    <div class="flex flex-1 items-center justify-center">
      <p class="text-slate-500">未找到题目</p>
    </div>
  }
</div>
